import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Endpoint } from '@/Config/api/endpoint';
import { UserApi } from '@/Domain/api/User/User.api';
import { User } from '@/Domain/api/User/dto/User.dto';

export const useUserAvatar = () => {
  const { t } = useTranslation('user_profile');
  const queryClient = useQueryClient();

  const uploadAvatarMutation = useMutation({
    mutationKey: [`${Endpoint.user.avatar}-upload`],
    mutationFn: (data: string) => UserApi.uploadAvatar(data),
    onSuccess: (data) => {
      queryClient.setQueryData([Endpoint.user.myself], () => data);
    },
    onError: () => {
      toast.error(t('toast.error_upload_avatar'));
    },
  });

  const deleteAvatarMutation = useMutation({
    mutationKey: [`${Endpoint.user.avatar}-delete`],
    mutationFn: () => UserApi.deleteAvatar(),
    onSuccess: () => {
      queryClient.setQueryData([Endpoint.user.myself], (prevData: User) => ({
        ...prevData,
        avatar_url: null,
      }));
    },
    onError: () => {
      toast.error(t('toast.error_delete_avatar'));
    },
  });

  const handleUpload = (file: string) => uploadAvatarMutation.mutate(file);

  const handleError = () => toast.error(t('toast.file_too_big'));

  const handleDelete = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.stopPropagation();

    await deleteAvatarMutation.mutateAsync();
  };

  return {
    handleError,
    handleUpload,
    handleDelete,
  };
};
